import { useState } from "react";
import { Product, OrderedProduct } from "src/shared/services/interfacev2";
import { ProductNote } from "src/shared/types";

/*** Safely extract Mongo ObjectId as string*/
const getProductId = (product: Product): string => {
  if (typeof product._id === "string") return product._id;
  return product._id?.$oid ?? "";
};

/*** Convert Product → OrderedProduct*/
const mapProductToOrderedProduct = (product: Product): OrderedProduct => {
  const unitPrice = Number(product.amount) || 0;

  return {
    productId: getProductId(product),
    name: product.productName,
    arabicName: product.arabicName || "",
    quantity: 1,
    unitPrice,
    lineTotal: unitPrice,
    type: product.type || "",
    notes: product.notes || "",
  };
};

export const useOrderManagement = () => {
  /** ✅ MUST be OrderedProduct[] */
  const [selectedProducts, setSelectedProducts] = useState<OrderedProduct[]>([]);
  const [discount, setDiscount] = useState<number>(0);
  const [deliveryFee, setDeliveryFee] = useState<number>(0);
  const [parcelFee, setParcelFee] = useState<number>(0);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editingNote, setEditingNote] = useState<string | null>(null);

  const calculateSubtotal = (): number => selectedProducts.reduce((sum, p) => sum + p.lineTotal, 0);

const calculateTotal = (subtotal: number, isDelivery: boolean = true): number => {
  return (
    subtotal
    - discount
    + (isDelivery ? deliveryFee : 0)
    + parcelFee   // ✅ THIS makes it reactive
  );
};

  /** Add product from menu (Product) */
  const addProduct = (product: Product) => {
    const id = getProductId(product);
    setSelectedProducts((prev) => {
      const existing = prev.find((p) => p.productId === id);
      if (existing) {
        return prev.map((p) => (p.productId === id ? { ...p, quantity: p.quantity + 1, lineTotal: (p.quantity + 1) * p.unitPrice } : p));
      }
      return [...prev, mapProductToOrderedProduct(product)];
    });
  };
  const ProductDecrement = (product: any) => {
    const productId = typeof product._id === "string" ? product._id : product._id?.$oid;

    const cartItem = selectedProducts.find((p) => p.productId === productId);

    if (cartItem) {
      decrementCart(cartItem);
    }
  };

  // Increment in cart
  const incrementCart = (item: OrderedProduct) => {
    setSelectedProducts((prev) =>
      prev.map((p) => (p.productId === item.productId ? { ...p, quantity: p.quantity + 1, lineTotal: (p.quantity + 1) * p.unitPrice } : p)),
    );
  };

  // Decrement in cart
  const decrementCart = (item: OrderedProduct) => {
    setSelectedProducts(
      (prev) =>
        prev
          .map((p) =>
            p.productId === item.productId
              ? {
                  ...p,
                  quantity: p.quantity - 1,
                  lineTotal: (p.quantity - 1) * p.unitPrice,
                }
              : p,
          )
          .filter((p) => p.quantity > 0), // 🔥 removes item when qty becomes 0
    );
  };

  /** Delete product from cart */
  const deleteProduct = (productId: string) => {
    setSelectedProducts((prev) => prev.filter((p) => p.productId !== productId));
  };

  /** Update notes for a product in cart */
  const handleNoteChange = (productId: string, note: ProductNote[]) => {
    setSelectedProducts((prev) => prev.map((p) => (p.productId === productId ? { ...p, notes: note } : p)));
  };

  return {
    selectedProducts,
    setSelectedProducts,
    discount,
    setDiscount,
    deliveryFee,
    setDeliveryFee,
    isEditing,
    setIsEditing,
    editingNote,
    setEditingNote,
    calculateSubtotal,
    calculateTotal,
    addProduct,
    ProductDecrement,
    incrementCart,
    decrementCart,
    deleteProduct,
    handleNoteChange,
    parcelFee,
    setParcelFee,
  };
};
